/**
 * ag-tabs.js — tab switcher for the AG · Tabs section.
 *
 * Progressive enhancement: the markup renders every panel, and this only
 * hides the inactive ones once it runs. Implements the WAI-ARIA tabs
 * keyboard pattern (arrows, Home, End) with roving tabindex.
 */
class AgTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (!this.tabs.length) return;

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));

    const active = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    this.activate(active === -1 ? 0 : active, false);
  }

  onClick(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;
    const index = this.tabs.indexOf(tab);
    if (index !== -1) this.activate(index);
  }

  onKeydown(event) {
    const tab = event.target.closest('[role="tab"]');
    if (!tab) return;

    const current = this.tabs.indexOf(tab);
    if (current === -1) return;

    let next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (current + 1) % this.tabs.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (current - 1 + this.tabs.length) % this.tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.activate(next);
    this.tabs[next].focus();
  }

  activate(index, focusable = true) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      // Roving tabindex: only the selected tab is in the tab order.
      tab.tabIndex = selected || !focusable ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });
  }
}

if (!customElements.get('ag-tabs')) {
  customElements.define('ag-tabs', AgTabs);
}
